/**
 * Tabs — the reference's tab strip, with a sliding orange ink bar under the active
 * tab (`variant="line"`) or a pill-shaped active background (`variant="pill"`).
 *
 * Ported 1:1 from `reference/tw-components.jsx`. Deviations are mechanical only:
 * `cx` and `Icon` are imported and the React hooks are named imports rather than
 * `React.useRef` / `React.useState` / `React.useLayoutEffect`.
 *
 * The ink bar is measured, not derived from the label: `useLayoutEffect` reads the
 * active button's `offsetLeft` / `offsetWidth` and writes them into inline `left` /
 * `width`, which is why the transition animates. It re-measures on `value` and on
 * `items`, so a tab set that changes length keeps the bar aligned.
 *
 * It renders `role="tablist"` / `role="tab"` but **does not** own the panels — the
 * caller renders those and is responsible for `role="tabpanel"` and the
 * `aria-controls` / `id` pair if it wants the full relationship. Upstream's PDP does
 * not wire those either.
 *
 * `variant="segmented"` adds the Returns-tab filter look: a sliding white pill on a
 * gray track, measured the same way as the line variant's ink bar (this is the
 * `retTabsV2`/`t-tabs-pill` pattern in the design storefront's Account Portal). Items
 * may carry a `count` for the small trailing badge.
 */
import { useLayoutEffect, useRef, useState } from "react";

import { cx } from "./cx";
import { Icon } from "./Icon.jsx";

export function Tabs({
  items = [],
  value,
  onChange,
  variant = "line",
  style = {},
  className = "",
}) {
  const listRef = useRef(null);
  const [ink, setInk] = useState({ left: 0, width: 0 });

  const [pill, setPill] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    if (variant !== "line" || !listRef.current) return;
    const active = listRef.current.querySelector(".is-active");
    if (active) setInk({ left: active.offsetLeft, width: active.offsetWidth });
  }, [value, items, variant]);

  useLayoutEffect(() => {
    if (variant !== "segmented" || !listRef.current) return;
    const active = listRef.current.querySelector(".is-active");
    if (active) setPill({ left: active.offsetLeft, width: active.offsetWidth });
  }, [value, items, variant]);

  if (variant === "segmented") {
    return (
      <div className={cx("dnd-tabs font-sans", className)} style={style}>
        <div
          ref={listRef}
          role="tablist"
          className="relative inline-flex items-center gap-[3px] p-[3px] rounded-[var(--radius-pill)] bg-neutral-100"
        >
          <span
            aria-hidden="true"
            className="absolute top-[3px] bottom-[3px] rounded-[var(--radius-pill)] bg-card shadow-sm [transition:left_var(--duration-base)_var(--ease-out),width_var(--duration-base)_var(--ease-out)]"
            style={{ left: pill.left, width: pill.width }}
          />
          {items.map((item) => {
            const on = item.value === value;
            return (
              <button
                key={item.value}
                role="tab"
                aria-selected={on}
                onClick={() => onChange?.(item.value)}
                className={cx(
                  "relative z-[1] inline-flex cursor-pointer items-center gap-[7px] border-0 bg-transparent h-[34px] px-[var(--space-4)] rounded-[var(--radius-pill)] font-sans text-sm font-semibold [transition:color_var(--duration-fast)]",
                  on ? "is-active text-ink" : "text-ink-2 hover:text-ink",
                  "focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                )}
              >
                {item.label}
                {item.count !== undefined && (
                  <span
                    className={cx(
                      "inline-flex items-center justify-center min-w-[18px] h-[18px] px-[5px] rounded-[var(--radius-pill)] text-2xs font-bold leading-none [transition:background_var(--duration-fast),color_var(--duration-fast)]",
                      on
                        ? "bg-blue-600 text-white"
                        : "bg-neutral-200 text-ink-3"
                    )}
                  >
                    {item.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={cx("dnd-tabs font-sans", className)} style={style}>
      <div
        ref={listRef}
        role="tablist"
        className={cx(
          "relative inline-flex",
          variant === "line"
            ? "gap-[var(--space-6)] border-b-[1.5px] border-line-subtle"
            : "gap-[var(--space-1)]"
        )}
      >
        {items.map((item) => {
          const on = item.value === value;
          return (
            <button
              key={item.value}
              role="tab"
              aria-selected={on}
              onClick={() => onChange?.(item.value)}
              className={cx(
                "relative inline-flex cursor-pointer items-center gap-[var(--space-2)] border-0 bg-transparent font-sans text-base [transition:color_var(--duration-fast)]",
                on
                  ? "is-active font-semibold text-blue-700"
                  : "font-medium text-ink-2 hover:text-ink",
                variant === "line"
                  ? "px-0 pb-[calc(var(--space-3)+1px)] pt-[var(--space-3)]"
                  : "rounded-[var(--radius-pill)] px-[var(--space-4)] py-[var(--space-2)]",
                variant === "pill" && on && "bg-blue-50",
                "focus-visible:rounded-sm focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
              )}
            >
              {item.icon && <Icon name={item.icon} size={20} weight={400} />}
              {item.label}
            </button>
          );
        })}
        {variant === "line" && (
          <span
            className="absolute bottom-[-1.5px] h-[3px] rounded-t-[3px] bg-orange-500 [transition:left_var(--duration-base)_var(--ease-out),width_var(--duration-base)_var(--ease-out)]"
            style={{ left: ink.left, width: ink.width }}
          />
        )}
      </div>
    </div>
  );
}
